import { supabase } from '@/lib/supabase'
import { resolvePollAction } from '@/lib/resolvers/pollResolver'
import type {
  PollingSnapshot,
  PartyAction,
  Party,
  Cohort,
  CohortPartyLoyalty,
  PartyDistrictState,
  Candidate,
  PollType,
  DetailLevel,
} from '@/types'

// Query key factory for consistent cache management
export const pollingSnapshotKeys = {
  all: ['pollingSnapshots'] as const,
  byParty: (partyId: string) => ['pollingSnapshots', 'party', partyId] as const,
  byPartyLimited: (partyId: string, limit: number) =>
    ['pollingSnapshots', 'party', partyId, 'limit', limit] as const,
  detail: (id: string) => ['pollingSnapshots', 'id', id] as const,
}

// ============================================================================
// Instant Poll Execution
// ============================================================================

export interface ExecutePollInput {
  partyId: string
  nationId: string
  tick: number
  scope: PollType
  detailLevel: DetailLevel
  targetId?: string
  targetName?: string
}

export interface ExecutePollResult {
  snapshot: PollingSnapshot
  action: PartyAction
}

interface PollResolutionData {
  party: Party
  allParties: Party[]
  targetCohorts: Cohort[]
  cohortLoyalties: CohortPartyLoyalty[]
  partyDistrictStates: PartyDistrictState[]
  candidates: Candidate[]
}

/**
 * Fetch all data needed for poll resolution
 */
async function fetchPollResolutionData(
  partyId: string,
  nationId: string,
  scope: PollType,
  targetId?: string
): Promise<PollResolutionData> {
  // Fetch the party
  const { data: party, error: partyError } = await supabase
    .from('parties')
    .select('*')
    .eq('id', partyId)
    .single()

  if (partyError) throw partyError

  // Fetch all active parties in the nation
  const { data: allParties, error: partiesError } = await supabase
    .from('parties')
    .select('*')
    .eq('nation_id', nationId)
    .eq('is_active', true)

  if (partiesError) throw partiesError

  // Fetch cohorts based on scope
  // We need to join through districts -> regions to filter by nation/region/district
  let cohortsQuery = supabase
    .from('cohorts')
    .select(`
      *,
      districts!inner (
        id,
        region_id,
        regions!inner (
          id,
          nation_id
        )
      )
    `)

  if (scope === 'national') {
    // All cohorts in the nation
    cohortsQuery = cohortsQuery.eq('districts.regions.nation_id', nationId)
  } else if (scope === 'regional') {
    // All cohorts in the target region
    cohortsQuery = cohortsQuery.eq('districts.region_id', targetId)
  } else if (scope === 'district') {
    // All cohorts in the target district
    cohortsQuery = cohortsQuery.eq('district_id', targetId)
  }

  const { data: cohortsWithJoins, error: cohortsError } = await cohortsQuery

  if (cohortsError) throw cohortsError

  // Strip the join data to get plain cohorts
  const targetCohorts: Cohort[] = (cohortsWithJoins || []).map(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ({ districts, ...cohort }) => cohort as Cohort
  )

  // Fetch cohort loyalties for these cohorts
  const cohortIds = targetCohorts.map((c) => c.id)
  const { data: loyalties, error: loyaltiesError } = await supabase
    .from('cohort_party_loyalties')
    .select('*')
    .in('cohort_id', cohortIds)

  if (loyaltiesError) throw loyaltiesError

  // Fetch party district states for all parties
  // We need recognition data for vote share calculation
  const partyIds = allParties.map((p) => p.id)
  const { data: districtStates, error: statesError } = await supabase
    .from('party_district_states')
    .select('*')
    .in('party_id', partyIds)

  if (statesError) throw statesError

  // Fetch active candidates for all parties
  const { data: candidates, error: candidatesError } = await supabase
    .from('candidates')
    .select('*')
    .in('party_id', partyIds)
    .eq('is_active', true)

  if (candidatesError) throw candidatesError

  return {
    party: party as Party,
    allParties: allParties as Party[],
    targetCohorts,
    cohortLoyalties: (loyalties || []) as CohortPartyLoyalty[],
    partyDistrictStates: (districtStates || []) as PartyDistrictState[],
    candidates: (candidates || []) as Candidate[],
  }
}

/**
 * Calculate AP cost for a poll based on scope and detail level
 */
function calculatePollCost(scope: PollType, detailLevel: DetailLevel): number {
  const baseCosts: Record<PollType, number> = {
    national: 1,
    regional: 1,
    district: 1,
  }
  const detailCosts: Record<DetailLevel, number> = {
    basic: 0,
    detailed: 1,
  }
  return baseCosts[scope] + detailCosts[detailLevel]
}

/**
 * Execute a poll instantly and return results
 *
 * This function:
 * 1. Verifies AP availability
 * 2. Fetches all data needed for poll resolution
 * 3. Creates the party_action record with status 'resolved'
 * 4. Runs the poll resolver to calculate results
 * 5. Creates the polling_snapshot record
 * 6. Deducts AP from the party
 * 7. Returns the snapshot and action
 *
 * All database operations happen in sequence to ensure consistency.
 * If any step fails, an error is thrown and no changes are committed.
 */
export async function executePollInstantly(
  input: ExecutePollInput
): Promise<ExecutePollResult> {
  const { partyId, nationId, tick, scope, detailLevel, targetId, targetName } = input

  // Calculate cost
  const totalCost = calculatePollCost(scope, detailLevel)

  // Step 1: Verify AP availability and get current party state
  const { data: currentParty, error: partyCheckError } = await supabase
    .from('parties')
    .select('action_points')
    .eq('id', partyId)
    .single()

  if (partyCheckError) throw partyCheckError
  if (currentParty.action_points < totalCost) {
    throw new Error(`Not enough Action Points. Need ${totalCost}, have ${currentParty.action_points}`)
  }

  // Step 2: Fetch all resolution data
  const resolutionData = await fetchPollResolutionData(partyId, nationId, scope, targetId)

  // Step 3: Create the party_action record with status 'resolved'
  // We create the action first so we have an ID for the resolver (used for noise seeding)
  const { data: action, error: actionError } = await supabase
    .from('party_actions')
    .insert({
      party_id: partyId,
      tick,
      action_type: 'poll',
      action_subtype: `${scope}_${detailLevel}`,
      target_type: scope === 'national' ? 'national' : scope === 'regional' ? 'region' : 'district',
      target_id: targetId || null,
      action_points_spent: totalCost,
      parameters: {
        scope,
        detail_level: detailLevel,
        target_name: targetName,
      },
      status: 'resolved', // Instantly resolved, not queued
      results: null, // Will be updated after resolution
    })
    .select()
    .single()

  if (actionError) throw actionError

  // Step 4: Run the poll resolver
  const { snapshot: snapshotData, summary } = resolvePollAction({
    action: action as PartyAction,
    party: resolutionData.party,
    allParties: resolutionData.allParties,
    targetCohorts: resolutionData.targetCohorts,
    cohortLoyalties: resolutionData.cohortLoyalties,
    partyDistrictStates: resolutionData.partyDistrictStates,
    candidates: resolutionData.candidates,
    nationId,
  })

  // Step 5: Create the polling_snapshot record
  const { data: snapshot, error: snapshotError } = await supabase
    .from('polling_snapshots')
    .insert(snapshotData)
    .select()
    .single()

  if (snapshotError) {
    // Clean up the action if snapshot creation fails
    await supabase.from('party_actions').delete().eq('id', action.id)
    throw snapshotError
  }

  // Step 6: Update the action with results (including snapshot ID)
  const { error: updateActionError } = await supabase
    .from('party_actions')
    .update({
      results: {
        snapshot_id: snapshot.id,
        summary,
      },
    })
    .eq('id', action.id)

  if (updateActionError) {
    // This is not fatal - the poll still completed
    console.error('Failed to update action results:', updateActionError)
  }

  // Step 7: Deduct AP from the party
  const { error: apError } = await supabase
    .from('parties')
    .update({ action_points: currentParty.action_points - totalCost })
    .eq('id', partyId)

  if (apError) {
    // This is problematic but we don't want to fail the whole operation
    // since the poll has already been created
    console.error('Failed to deduct AP:', apError)
    throw apError
  }

  return {
    snapshot: snapshot as PollingSnapshot,
    action: { ...action, results: { snapshot_id: snapshot.id, summary } } as PartyAction,
  }
}

/**
 * Fetch polling snapshots for a party, ordered by tick (most recent first)
 */
export async function fetchPollingSnapshots(
  partyId: string,
  limit: number = 10
): Promise<PollingSnapshot[]> {
  const { data, error } = await supabase
    .from('polling_snapshots')
    .select('*')
    .eq('party_id', partyId)
    .order('tick', { ascending: false })
    .limit(limit)

  if (error) {
    throw error
  }

  return data as PollingSnapshot[]
}

/**
 * Fetch a single polling snapshot by ID
 */
export async function fetchPollingSnapshot(id: string): Promise<PollingSnapshot | null> {
  const { data, error } = await supabase
    .from('polling_snapshots')
    .select('*')
    .eq('id', id)
    .single()

  if (error) {
    if (error.code === 'PGRST116') {
      return null
    }
    throw error
  }

  return data as PollingSnapshot
}
